import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import DataSource from 'devextreme/data/data_source';
import { Subject } from 'rxjs';
import { MasterDataEditService } from '../../../../api/master-data-edit.service';
import { EditDropdownModel, ProductMasterDataModel, ProductMasterWithSizeListGrid, ProductSizeGridModel, RenameDataModel } from '../../../../shared/models/product.model';
import { takeUntil } from 'rxjs/operators';
import { RenameBrandComponent, RenameCompanyComponent, RenameMasterBrandComponent, RenameProductComponent, RenameSubBrandComponent } from '../rename-popups';
import { User } from '../../../../shared';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { LoaderService } from '../../../../core/loader/loader.service';
import { NotificationService } from '../../../../service/notification.service';
import { NotificationType } from '../../../../shared/enum/notification.enum';

@Component({
  selector: 'app-product-edit',
  templateUrl: './product-edit.component.html',
})
export class ProductEditComponent implements OnInit, OnDestroy {
  @Input() data: ProductMasterWithSizeListGrid;
  @Input() currentUser: User;
  @Input() isCloning = false;

  @ViewChild('companySelectBox') companySelectBox: DxSelectBoxComponent;
  @ViewChild('masterBrandSelectBox') masterBrandSelectBox: DxSelectBoxComponent;
  @ViewChild('brandSelectBox') brandSelectBox: DxSelectBoxComponent;
  @ViewChild('subBrandSelectBox') subBrandSelectBox: DxSelectBoxComponent;
  @ViewChild('productSelectBox') productSelectBox: DxSelectBoxComponent;

  public companyData: any;
  public selectedEditCompany = null;
  private companyAllData: EditDropdownModel[];

  public masterBrandData: EditDropdownModel[] = [];
  public selectedEditMasterBrand = null;

  public brandData: EditDropdownModel[] = [];
  public selectedEditBrand = null;

  public subBrandData: EditDropdownModel[] = [];
  public selectedEditSubBrand = null;

  public productData: any;
  public selectedEditProduct = null;

  public categoryData: EditDropdownModel[] = [];
  public selectedEditCategory = null;

  public subcategoryFunctionData: EditDropdownModel[] = [];
  public selectedEditSubcategoryFunction = null;

  public subcategoryFormData: EditDropdownModel[] = [];
  public selectedEditSubcategoryForm = null;

  public productSizeOnLabelData: any;
  public productSizeOnLabelDataAll: ProductSizeGridModel[] = [];
  public selectedEditProductSizeOnLabel = null;
  public productIsMensProduct = false;
  public productIsNatureInspired = false;

  public showDropDownButtonCompany = true;
  public showDropDownButtonMasterBrand = true;
  public showDropDownButtonBrand = true;
  public showDropDownButtonSubBrand = true;
  public showDropDownButtonProduct = true;

  public companyBrandDDLLoading = true;
  public masterBrandDDLLoading = true;
  public brandDataDDLLoading = true;
  public subBrandDataDDLLoading = true;
  public productDataDDLLoading = true;
  public subcategoryFunctionDataDLLoading = true;
  public subcategoryFormDataDLLoading = true;
  public categoryDataDLLoading = true;
  public productSizeDataDLLoading = true;

  private _destroyed$ = new Subject();
  private isEditMode: boolean;
  private productAllData: EditDropdownModel[];
  onEditDDLText: any;

  constructor(
	private masterDataEditService: MasterDataEditService,
	public activeModal: NgbActiveModal,
	private modalService: NgbModal,
	private loaderService: LoaderService,
	private notificationService: NotificationService,
  ) { }

  ngOnInit() {
	this.fetchCompanyData();
	if (this.data) {
		this.setDefaultData();
	} else {
		this.isEditMode = true;
		this.fetchCategoryData();
		this.fetchProductSizeOnLabelData();
	}
  }

  ngOnDestroy(): void {
	this.loaderService.hide();
	this._destroyed$.next(null);
	this._destroyed$.complete();
  }

  fetchCompanyData() {
	this.companyBrandDDLLoading = true;
	this.masterDataEditService.getCompany()
	.pipe(takeUntil(this._destroyed$))
	.subscribe((res: EditDropdownModel[]) => {
		this.companyAllData = res;
		this.companyData = new DataSource({
		store: {
			data: res,
			type: 'array'
		},
		paginate: true
		});
		this.companyBrandDDLLoading = false;
	});
  }

  editCompanySelected(event) {
	const selectedItem = event.selectedItem;
	let companyId;
	if (selectedItem && selectedItem.Id && this.selectedEditCompany !== selectedItem.Id) {
		companyId = selectedItem.Id;
		const currentCompanyItem = this.companyAllData.find(item => item.Id === companyId);
		if (currentCompanyItem && currentCompanyItem.IsNew) {
		this.showDropDownButtonCompany = false;
		this.resetCompanyChildItems();
		} else {
		this.showDropDownButtonCompany = true;
		}
		this.selectedEditCompany = companyId;
		this.fetchMasterBrandData(companyId, true);
	} else {
		event.selectedItem = {
		Id: this.selectedEditCompany,
		Name: selectedItem
		};
	}
  }

  addCustomCompanyItem(data) {
	if (!data.text) {
		data.customItem = null;
		return;
	}
	const companyId = this.selectedEditCompany;
	const company = this.companyAllData.find(com => com.Id === companyId);

	if (!company || !this.data) {
		const maxId = this.companyAllData.length > 0 ? Math.max(...this.companyAllData.map(o => o.Id)) : 999999;
		const newId = maxId + 1;
		this.companyAllData.push({
		Id: newId,
		Name: data.text,
		IsNew: true,
		});
		this.companySelectBox.instance.option('value', newId);
		if (!this.data) {
		this.resetCompanyChildItems();
		}
		return;
	}

	if (!this.showDropDownButtonCompany) {
		this.setNewCompanyItem(data);
		return;
	}

	if (this.data) {
		const newData: RenameDataModel = {};
		newData.Id = companyId;
		newData.NewName = data.text;
		newData.ModifiedByUserId = this.currentUser.UserId;
		const modalRef = this.modalService.open(RenameCompanyComponent, {windowClass: 'rename-container', backdrop: false});
		modalRef.componentInstance.selectedEditCompanyOldName = company.Name;
		modalRef.componentInstance.selectedEditCompanyNewName = newData.NewName;
		modalRef.componentInstance.companyId = companyId;
		modalRef.result.then(result => {
		// onclose
		if (result === 'save') {
			this.masterDataEditService.updateCompanyName(newData)
			.pipe(takeUntil(this._destroyed$))
			.subscribe((res: boolean) => {
				this.fetchCompanyData();
				this.companySelectBox.instance.option('value', companyId);
			});
		}
		if (result === 'cancel') {
			this.selectedEditCompany = data.text;
		}
		}, (reason) => {
		this.setNewCompanyItem(data);
		this.resetCompanyChildItems();
		});
	}
  }

  private resetCompanyChildItems() {
	this.masterBrandData = [];
	this.selectedEditMasterBrand = null;
	this.brandData = [];
	this.selectedEditBrand = null;
	this.subBrandData = [];
	this.selectedEditSubBrand = null;
	this.productData = [];
	this.selectedEditProduct = null;
  }

  private setNewCompanyItem(data) {
	const maxId = this.companyAllData.length > 0 ? Math.max(...this.companyAllData.map(o => o.Id)) : 999999;
	const newId = maxId + 1;

	this.companyAllData.push({
		Id: newId,
		Name: data.text,
		IsNew: true,
	});

	this.companyData = new DataSource({
		store: {
		data: this.companyAllData,
		type: 'array'
		},
		paginate: true
	});
	this.selectedEditCompany = newId;
	this.showDropDownButtonCompany = false;
  }

  fetchMasterBrandData(companyId, isParentChanged = false) {
	if (!companyId || companyId < 1) {
		return;
	}
	this.masterBrandDDLLoading = true;
	this.masterDataEditService.getMasterBrand(companyId)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.masterBrandDDLLoading = false;
		if (res.length > 0) {
			this.masterBrandData = res;
			if (isParentChanged) {
			const masterBrandId = res[0].Id;
			this.selectedEditMasterBrand = masterBrandId;
			this.fetchBrandData(companyId, masterBrandId, true);
			}
		} else {
			this.masterBrandData = [];
			this.selectedEditMasterBrand = null;
			this.fetchBrandData(companyId, -1, true);
		}
		});
  }

  onEditMasterBrandSelected(event) {
	const selectedItem = event.selectedItem;
	let masterBrandId;
	if (selectedItem && selectedItem.Id && this.selectedEditMasterBrand !== selectedItem.Id) {
		masterBrandId = selectedItem.Id;
		const currentMasterBrandItem = this.masterBrandData.find(item => item.Id === masterBrandId);
		if (currentMasterBrandItem && currentMasterBrandItem.IsNew) {
		this.showDropDownButtonMasterBrand = false;
		this.resetMasterBrandChildItems();
		} else {
		this.showDropDownButtonMasterBrand = true;
		}
		this.selectedEditMasterBrand = masterBrandId;
	} else {
		const currentMasterBrandItem = this.masterBrandData.find(item => item.Id === this.selectedEditMasterBrand);
		if (!currentMasterBrandItem) {
		event.selectedItem = {
			Id: this.selectedEditMasterBrand,
			Name: selectedItem
		};
		}
	}
	this.fetchBrandData(this.selectedEditCompany, masterBrandId, true);
  }

  addCustomMasterBrandItem(data) {
	if (!data.text) {
		data.customItem = null;
		return;
	}

	const masterBrandId = this.selectedEditMasterBrand;
	const masterBrand = this.masterBrandData.find(item => item.Id === masterBrandId);
	if (!masterBrand || !this.data) {
		const maxId = this.masterBrandData.length > 0 ? Math.max(...this.masterBrandData.map(o => o.Id)) : 999999;
		const newId = maxId + 1;
		this.masterBrandData.push({
		Id: newId,
		Name: data.text,
		IsNew: true,
		});
		this.masterBrandSelectBox.instance.option('value', newId);
		if (!this.data) {
		this.resetMasterBrandChildItems();
		}
		return;
	}
	if (!this.showDropDownButtonMasterBrand || !this.showDropDownButtonCompany) {
		this.setNewMasterBrand(data);
		return;
	}
	const newData: RenameDataModel = {};
	newData.Id = masterBrandId;
	newData.NewName = data.text;
	newData.ModifiedByUserId = this.currentUser.UserId;

	const modalRef = this.modalService.open(RenameMasterBrandComponent, {windowClass: 'rename-container', backdrop: false});
	modalRef.componentInstance.selectedEditMasterBrandOldName = masterBrand.Name;
	modalRef.componentInstance.selectedEditMasterBrandNewName = newData.NewName;
	modalRef.componentInstance.masterBrandId = masterBrandId;
	modalRef.result.then(result => {
		// onclose
		if (result === 'save') {
		this.masterDataEditService.updateMasterBrandName(newData)
			.pipe(takeUntil(this._destroyed$))
			.subscribe((res: boolean) => {
			this.masterBrandSelectBox.instance.option('value', masterBrandId);
			});
		}
		if (result === 'cancel') {
		this.setNewMasterBrand(data);
		}
	}, (reason) => {
		// ondismiss
		this.setNewMasterBrand(data);
		this.resetMasterBrandChildItems();
	});
  }

  private resetMasterBrandChildItems() {
	this.brandData = [];
	this.selectedEditBrand = null;
	this.subBrandData = [];
	this.selectedEditSubBrand = null;
	this.productData = [];
	this.selectedEditProduct = null;
  }

  private setNewMasterBrand(data) {
	const maxId = this.masterBrandData.length > 0 ? Math.max(...this.masterBrandData.map(o => o.Id)) : 999999;
	const newId = maxId + 1;
	this.masterBrandData.push({
		Id: newId,
		Name: data.text,
		IsNew: true,
	});
	this.selectedEditMasterBrand = newId;
	this.showDropDownButtonMasterBrand = false;
  }

  fetchBrandData(companyId, masterBrandId, isParentChanged = false) {
	if (!masterBrandId || !companyId || masterBrandId < 1 || companyId < 1) {
		return;
	}
	this.brandDataDDLLoading = true;
	this.masterDataEditService.getBrand(companyId, masterBrandId)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.brandDataDDLLoading = false;
		if (res.length > 0) {
			this.brandData = res;
			if (isParentChanged) {
			const brandId = res[0].Id;
			this.selectedEditBrand = brandId;
			this.fetchSubBrandData(companyId, masterBrandId, brandId, true);
			}
		} else {
			this.brandData = [];
			this.selectedEditBrand = null;
			this.fetchSubBrandData(companyId, masterBrandId, -1, true);
		}
		});
  }

  onEditBrandSelected(event) {
	const selectedItem = event.selectedItem;
	let brandId;
	if (selectedItem && selectedItem.Id && this.selectedEditBrand !== selectedItem.Id) {
		brandId = selectedItem.Id;
		const currentBrandItem = this.brandData.find(item => item.Id === brandId);
		if (currentBrandItem && currentBrandItem.IsNew) {
		this.showDropDownButtonBrand = false;
		this.resetBrandChildItems();
		} else {
		this.showDropDownButtonBrand = true;
		}
		this.selectedEditBrand = brandId;
		this.fetchSubBrandData(this.selectedEditCompany, this.selectedEditMasterBrand, this.selectedEditBrand, true);
	} else {
		event.selectedItem = {
		Id: this.selectedEditBrand,
		Name: selectedItem
		};
	}
  }

  addCustomBrandItem(data) {
	if (!data.text) {
		data.customItem = null;
		return;
	}
	const brandId = this.selectedEditBrand;
	const brand = this.brandData.find(item => item.Id === brandId);

	if (!brand || !this.data) {
		const maxId = this.brandData.length > 0 ? Math.max(...this.brandData.map(o => o.Id)) : 999999;
		const newId = maxId + 1;
		this.brandData.push({
		Id: newId,
		Name: data.text,
		IsNew: true,
		});

		this.brandSelectBox.instance.option('value', newId);
		if (!this.data) {
		this.resetBrandChildItems();
		}
		return;
	}
	if (!this.showDropDownButtonBrand ||
		!this.showDropDownButtonCompany ||
		!this.showDropDownButtonMasterBrand) {
		this.setNewBrandItem(data);
		return;
	}
	const newData: RenameDataModel = {};
	newData.Id = brandId;
	newData.NewName = data.text;
	newData.ModifiedByUserId = this.currentUser.UserId;

	const modalRef = this.modalService.open(RenameBrandComponent, {windowClass: 'rename-container', backdrop: false});
	modalRef.componentInstance.selectedEditBrandOldName = brand.Name;
	modalRef.componentInstance.selectedEditBrandNewName = newData.NewName;
	modalRef.componentInstance.brandId = brandId;
	modalRef.result.then(result => {
		// onclose
		if (result === 'save') {
		this.masterDataEditService.updateBrandName(newData)
			.pipe(takeUntil(this._destroyed$))
			.subscribe((res: boolean) => {
			this.brandSelectBox.instance.option('value', brandId);
			});
		}
		if (result === 'cancel') {
		this.setNewBrandItem(data);
		}
	}, (reason) => {
		// ondismiss
		this.setNewBrandItem(data);
		this.resetBrandChildItems();
	});
  }

  resetBrandChildItems() {
	this.subBrandData = [];
	this.selectedEditSubBrand = null;
	this.productData = [];
	this.selectedEditProduct = null;
  }

  private setNewBrandItem(data) {
	const maxId = this.brandData.length > 0 ? Math.max(...this.brandData.map(o => o.Id)) : 999999;
	const newId = maxId + 1;
	// this.brandData = [];
	this.brandData.push({
		Id: newId,
		Name: data.text,
		IsNew: true,
	});
	this.selectedEditBrand = newId;
	this.showDropDownButtonBrand = false;
  }

  fetchSubBrandData(companyId, masterBrandId, brandId, isParentChanged = false) {
	this.subBrandDataDDLLoading = true;
	this.masterDataEditService.getSubBrand(companyId, masterBrandId, brandId)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.subBrandDataDDLLoading = false;
		if (res.length > 0) {
			this.subBrandData = res;
			if (isParentChanged) {
			const subBrandId = res[0].Id;
			this.selectedEditSubBrand = subBrandId;
			this.fetchProductData(companyId, masterBrandId, brandId, subBrandId, true);
			}
		} else {
			this.subBrandData = [];
			this.selectedEditSubBrand = null;
			this.fetchProductData(companyId, masterBrandId, brandId, -1, true);
		}
		});
  }

  onEditSubBrandSelected(event) {
	const selectedItem = event.selectedItem;
	let subBrandId;
	if (selectedItem && selectedItem.Id && this.selectedEditSubBrand !== selectedItem.Id) {
		subBrandId = selectedItem.Id;
		const currentSubBrandItem = this.subBrandData.find(item => item.Id === subBrandId);
		if (currentSubBrandItem && currentSubBrandItem.IsNew) {
		this.showDropDownButtonSubBrand = false;
		this.resetSubBrandChildItems();
		} else {
		this.showDropDownButtonSubBrand = true;
		}
		this.selectedEditSubBrand = subBrandId;
		this.fetchProductData(this.selectedEditCompany, this.selectedEditMasterBrand, this.selectedEditBrand, subBrandId, true);
	} else {
		event.selectedItem = {
		Id: this.selectedEditSubBrand,
		Name: selectedItem
		};
	}
  }

  addCustomSubBrandItem(data) {
	if (!data.text) {
		data.customItem = null;
		return;
	}
	const subBrandId = this.selectedEditSubBrand;
	const subBrand = this.subBrandData.find(item => item.Id === subBrandId);

	if (!subBrand || !this.data) {
		const maxId = this.subBrandData.length > 0 ? Math.max(...this.subBrandData.map(o => o.Id)) : 999999;
		const newId = maxId + 1;
		this.subBrandData.push({
		Id: newId,
		Name: data.text,
		IsNew: true,
		});
		this.subBrandSelectBox.instance.option('value', newId);
		if (!this.data) {
		this.resetSubBrandChildItems();
		}
		return;
	}
	if (!this.showDropDownButtonSubBrand ||
		!this.showDropDownButtonCompany ||
		!this.showDropDownButtonMasterBrand ||
		!this.showDropDownButtonBrand) {
		this.setNewSubBrandItem(data);
		return;
	}
	const newData: RenameDataModel = {};
	newData.Id = subBrandId;
	newData.NewName = data.text;
	newData.ModifiedByUserId = this.currentUser.UserId;

	const modalRef = this.modalService.open(RenameSubBrandComponent, {windowClass: 'rename-container', backdrop: false});
	modalRef.componentInstance.selectedEditSubBrandOldName = subBrand.Name;
	modalRef.componentInstance.selectedEditSubBrandNewName = newData.NewName;
	modalRef.componentInstance.subBrandId = subBrandId;
	modalRef.result.then(result => {
		// onclose
		if (result === 'save') {
		this.masterDataEditService.updateSubBrandName(newData)
			.pipe(takeUntil(this._destroyed$))
			.subscribe((res: boolean) => {
			this.subBrandSelectBox.instance.option('value', subBrandId);
			});
		}
		if (result === 'cancel') {
		this.setNewSubBrandItem(data);
		}
	}, (reason) => {
		// ondismiss
		this.setNewSubBrandItem(data);
		this.resetSubBrandChildItems();
	});
  }

  private resetSubBrandChildItems() {
	this.productData = [];
	this.selectedEditProduct = null;
  }

  private setNewSubBrandItem(data) {
	const maxId = this.subBrandData.length > 0 ? Math.max(...this.subBrandData.map(o => o.Id)) : 999999;
	const newId = maxId + 1;
	// this.subBrandData = [];
	this.subBrandData.push({
		Id: newId,
		Name: data.text,
		IsNew: true,
	});
	this.selectedEditSubBrand = newId;
	this.showDropDownButtonSubBrand = false;
  }

  fetchProductData(companyId, masterBrandId, brandId, subBrandId, isParentChanged = false) {
	this.productDataDDLLoading = true;
	this.masterDataEditService.getProduct(companyId, masterBrandId, brandId, subBrandId)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.productDataDDLLoading = false;
		if (res.length > 0) {
			this.productAllData = res;
			this.productData = new DataSource({
			store: {
				data: res,
				type: 'array'
			},
			paginate: true
			});
			if (isParentChanged) {
			const productId = res[0].Id;
			this.selectedEditProduct = productId;
			}
		} else {
			this.productAllData = [];
			this.productData = new DataSource({
			store: {
				data: [],
				type: 'array'
			},
			paginate: true
			});
			this.selectedEditProduct = null;
		}
		});
  }

  onEditProductSelected(event) {
	const selectedItem = event.selectedItem;
	let productId;
	if (selectedItem && selectedItem.Id && this.selectedEditProduct !== selectedItem.Id) {
		productId = selectedItem.Id;
		// const currentProductItem = this.productAllData.find(item => item.Id === productId);
		// if (currentProductItem && currentProductItem.IsNew) {
		//   this.showDropDownButtonProduct = false;
		// } else {
		//   this.showDropDownButtonProduct = true;
		// }
		this.selectedEditProduct = productId;
	} else {
		event.selectedItem = {
		Id: this.selectedEditProduct,
		Name: selectedItem
		};
	}
  }

  addCustomProductItem(data) {
	if (!data.text) {
		data.customItem = null;
		return;
	}
	const productId = this.selectedEditProduct;
	const product = this.productAllData.find(item => item.Id === productId);

	if (!product || !this.data) {
		const maxId = this.productAllData.length > 0 ? Math.max(...this.productAllData.map(o => o.Id)) : 999999;
		const newId = maxId + 1;
		this.productAllData.push({
		Id: newId,
		Name: data.text,
		IsNew: true
		});
		this.productData = new DataSource({
		store: {
			data: this.productAllData,
			type: 'array'
		},
		paginate: true
		});
		this.productSelectBox.instance.option('value', newId);
		return;
	}
	if (!this.showDropDownButtonProduct ||
		!this.showDropDownButtonCompany ||
		!this.showDropDownButtonMasterBrand ||
		!this.showDropDownButtonBrand ||
		!this.showDropDownButtonSubBrand) {
		this.setNewProductItem(data);
		return;
	}
	const newData: RenameDataModel = {};
	newData.Id = productId;
	newData.NewName = data.text;
	newData.ModifiedByUserId = this.currentUser.UserId;

	const modalRef = this.modalService.open(RenameProductComponent, {windowClass: 'rename-container', backdrop: false});
	modalRef.componentInstance.selectedEditProductOldName = product.Name;
	modalRef.componentInstance.selectedEditProductNewName = newData.NewName;
	modalRef.componentInstance.productId = productId;
	modalRef.result.then(result => {
		// onclose
		if (result === 'save') {
		this.masterDataEditService.updateProductName(newData)
			.pipe(takeUntil(this._destroyed$))
			.subscribe((res: boolean) => {
			this.productSelectBox.instance.option('value', productId);
			});
		}
		if (result === 'cancel') {
		this.setNewProductItem(data);
		}
	}, (reason) => {
		// ondismiss
		this.setNewProductItem(data);
		// this.selectedEditProduct = null;
	});
  }

  private setNewProductItem(data) {
	const maxId = this.productAllData.length > 0 ? Math.max(...this.productAllData.map(o => o.Id)) : 999999;
	const newId = maxId + 1;
	this.productAllData.push({
		Id: newId,
		Name: data.text,
		IsNew: true,
	});

	this.productData = new DataSource({
		store: {
		data: this.productAllData,
		type: 'array'
		},
		paginate: true
	});
	this.selectedEditProduct = newId;
	this.showDropDownButtonProduct = false;
  }

  fetchCategoryData() {
	this.categoryDataDLLoading = true;
	this.masterDataEditService.getCategory()
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.categoryDataDLLoading = false;
		if (res.length > 0) {
			this.categoryData = res;
		}
		});
  }

  onEditCategorySelected(event) {
	const categoryId = event.value;
	this.selectedEditCategory = categoryId;
	this.fetchSubcategoryFunctionData(categoryId, true);
  }

  fetchSubcategoryFunctionData(categoryId, isParentChanged = false) {
	if (categoryId) {
		this.subcategoryFunctionDataDLLoading = true;
		this.masterDataEditService.getSubcategoryFunction(categoryId)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
			this.subcategoryFunctionDataDLLoading = false;
			if (res.length > 0) {
			this.subcategoryFunctionData = res;
			if (isParentChanged) {
				const subcategoryFunctionId = res[0].Id;
				this.selectedEditSubcategoryFunction = subcategoryFunctionId;
				this.fetchSubcategoryFormData(categoryId, subcategoryFunctionId, true);
			}
			}
		});
	}
  }

  onEditSubcategoryFunctionSelected(event) {
	const subcategoryFunctionId = event.value;
	this.selectedEditSubcategoryFunction = subcategoryFunctionId;
	this.fetchSubcategoryFormData(this.selectedEditCategory, subcategoryFunctionId, true);
  }

  fetchSubcategoryFormData(categoryId, subcategoryFunctionId: number, isParentChanged = false) {
	if (subcategoryFunctionId || categoryId || subcategoryFunctionId < 1 || categoryId < 1 || isParentChanged) {
		this.subcategoryFormDataDLLoading = true;
		this.masterDataEditService.getSubcategoryForm(categoryId, subcategoryFunctionId)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
			this.subcategoryFormDataDLLoading = false;
			if (res.length > 0) {
			this.subcategoryFormData = res;
			if (isParentChanged) {
				const subcategoryFormId = res[0].Id;
				this.selectedEditSubcategoryForm = subcategoryFormId;
			}
			}
		});
	}
  }

  onEditSubcategoryFormSelected(event) {
	const subcategoryFormId = event.value;
	this.selectedEditSubcategoryForm = subcategoryFormId;
  }

  fetchProductSizeOnLabelData() {
	this.productSizeDataDLLoading = true;
	this.masterDataEditService.getProductSizeOnLabel()
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res: ProductSizeGridModel[]) => {
		this.productSizeDataDLLoading = false;
		if (res.length > 0) {
			this.productSizeOnLabelDataAll = res;
			this.productSizeOnLabelData = new DataSource({
			store: {
				data: res,
				type: 'array'
			},
			paginate: true
			});
		}
		});
  }

  onProductSizeOnLabelSelected(event) {
	const productSizeId = event.selectedItem;
	if (productSizeId && this.selectedEditProductSizeOnLabel !== productSizeId.ProductSizeId) {
		this.selectedEditProductSizeOnLabel = productSizeId.ProductSizeId;
	}
  }

  addCustomProductSizeOnLabelItem(data) {
	const maxId = this.productSizeOnLabelDataAll.length > 0 ? Math.max(...this.productSizeOnLabelDataAll.map(o => o.ProductSizeId)) : 99999;
	const newId = maxId + 1;
	this.productSizeOnLabelData = null;

	this.productSizeOnLabelDataAll.push({
		ProductSizeId: newId,
		ProductSizeByMetric: data.text,
		IsNew: true
	});

	this.productSizeOnLabelData = new DataSource({
		store: {
		data: this.productSizeOnLabelDataAll,
		type: 'array'
		},
		paginate: true
	});
	this.selectedEditProductSizeOnLabel = newId;
  }

  saveProductMasterData() {
	this.loaderService.show();
	const saveData: ProductMasterDataModel = {};

	const companyId = this.selectedEditCompany;
	const company = this.companyAllData.find(com => com.Id === companyId);

	const masterBrandId = this.selectedEditMasterBrand;
	const masterBrand = this.masterBrandData.find(m => m.Id === masterBrandId);


	const productSizeId = this.selectedEditProductSizeOnLabel;
	const productSize = this.productSizeOnLabelDataAll.find(p => p.ProductSizeId === productSizeId);

	const subBrandId = this.selectedEditSubBrand;
	const subBrand = this.subBrandData.find(s => s.Id === subBrandId);

	const categoryId = this.selectedEditCategory;
	const productId = this.selectedEditProduct;
	const product = this.productAllData.find(p => p.Id === productId);

	const brandId = this.selectedEditBrand;
	const brand = this.brandData.find(b => b.Id === brandId);

	saveData.ProductMasterId = (this.data && !this.isCloning) ? this.data.ProductMasterId : null;
	saveData.CompanyName = company ? company.Name : null;
	saveData.MasterBrandName = masterBrand ? masterBrand.Name : null;
	saveData.BrandName = brand ? brand.Name : null;
	saveData.SubBrandName = subBrand ? subBrand.Name : null;
	saveData.ProductName = product ? product.Name : null;
	saveData.CategoryId = categoryId ? categoryId : null;
	saveData.SubcategoryFunctionId = this.selectedEditSubcategoryFunction;
	saveData.SubcategoryFormId = this.selectedEditSubcategoryForm;
	saveData.NatureInspired = this.productIsNatureInspired;
	saveData.MensProduct = this.productIsMensProduct;
	saveData.ProductSizeIdList = (productSize && productSize.ProductSizeId && !productSize.IsNew) ? String(productSizeId) : '1';
	saveData.UserId = this.currentUser.UserId;
	saveData.TransactionLevel = null;

	this.masterDataEditService.createOrModifyProductMaster(saveData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.notificationService.show('Product created', NotificationType.Success);
		this.loaderService.hide();
		this.activeModal.dismiss('saved');
		});
  }

  private setDefaultData() {
	this.selectedEditCompany = this.data ? this.data.CompanyId : null;
	this.selectedEditMasterBrand = this.data ? this.data.MasterBrandId : null;
	this.selectedEditBrand = this.data ? this.data.BrandId : null;
	this.selectedEditSubBrand = this.data ? this.data.SubBrandId : null;
	this.selectedEditProduct = this.data ? this.data.ProductId : null;
	this.selectedEditCategory = this.data ? this.data.CategoryId : null;
	this.selectedEditSubcategoryFunction = this.data ? this.data.SubcategoryFunctionId : null;
	this.selectedEditSubcategoryForm = this.data ? this.data.SubcategoryFormId : null;
	this.selectedEditProductSizeOnLabel = this.data && this.data.ProductSizeId > 0 ? this.data.ProductSizeId : null;
	this.productIsMensProduct = this.data ? this.data.ProductIsMensProduct : false;
	this.productIsNatureInspired = this.data ? this.data.ProductIsNatureInspired : false;
	this.fetchMasterBrandData(this.selectedEditCompany);
	this.fetchBrandData(this.selectedEditCompany, this.selectedEditMasterBrand);
	this.fetchSubBrandData(this.selectedEditCompany, this.selectedEditMasterBrand, this.selectedEditBrand);
	this.fetchProductData(this.selectedEditCompany, this.selectedEditMasterBrand, this.selectedEditBrand, this.selectedEditSubBrand);

	this.fetchCategoryData();
	this.fetchSubcategoryFunctionData(this.selectedEditCategory);
	this.fetchSubcategoryFormData(this.selectedEditCategory, this.selectedEditSubcategoryFunction);

	this.fetchProductSizeOnLabelData();
  }
}
